import api from '../api/axios';

/**
 * Reading an office code and turning it into a punch.
 *
 * Shared, because two screens do it: the Attendance page, and the phone home
 * screen — which opens the scanner where the person is standing rather than
 * routing them through a page they never asked for and bouncing them back.
 * Two copies of this would be two sets of error messages drifting apart, and
 * the messages are most of the value.
 */

/** The browser's position, or nothing, within a short budget. */
const positionWithin = (ms) => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve({}); return; }
  let settled = false;
  const done = (v) => { if (!settled) { settled = true; resolve(v); } };
  /*
   * A code already proves where somebody is, so position here is a nice-to-
   * have for the record rather than the check. Waiting on a phone that cannot
   * get a fix would turn a one-second punch into a thirty-second one.
   */
  setTimeout(() => done({}), ms);
  navigator.geolocation.getCurrentPosition(
    (p) => done({
      latitude: p.coords.latitude,
      longitude: p.coords.longitude,
      accuracy: p.coords.accuracy,
    }),
    () => done({}),
    { enableHighAccuracy: true, timeout: ms, maximumAge: 0 }
  );
});

/**
 * What the camera read.
 *
 * The code holds a full URL. Parsed rather than pattern-matched, so a scan of
 * a different QR code says so instead of being half-understood.
 */
export const parseQr = (text) => {
  try {
    const u = new URL(text);
    const token = u.searchParams.get('qr');
    const loc = u.searchParams.get('loc');
    if (token && loc) return { token, loc };
  } catch { /* not a URL at all */ }
  return null;
};

/**
 * Punch with a scanned code.
 *
 * Always resolves — never throws — because every caller needs the failure as
 * something to show, not something to catch.
 */
export const punchWithCode = async (token, locationId) => {
  const position = await positionWithin(2500);
  try {
    const { data } = await api.post('/attendance/punch/scan', {
      qr_token: token, location_id: locationId, ...position,
    });
    return { status: 'done', ...data };
  } catch (e) {
    return {
      status: 'failed',
      message: e.response?.data?.message || 'That did not go through.',
      expired: !!e.response?.data?.expired_code,
    };
  }
};

/** Scanned text straight to a result, for callers with nothing to decide. */
export const punchFromScan = async (text) => {
  const parsed = parseQr(text);
  if (!parsed) {
    return {
      status: 'failed',
      message: 'That is not a Tychi Desk attendance code. Scan the code on the office screen.',
    };
  }
  return punchWithCode(parsed.token, parsed.loc);
};
